<template>
  <div class="flex min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-300">
    <!-- Sidebar -->
    <AcdSidebar :isOpen="isSidebarOpen" />

    <!-- Main Content -->
    <main
      class="flex-1 overflow-x-hidden transition-all duration-300"
      :class="{ 'ml-64': isSidebarOpen, 'ml-0': !isSidebarOpen }"
    >
      <header
        class="fixed top-0 z-50 w-full bg-white dark:bg-gray-800 shadow-sm transition-all duration-300"
      >
        <AcdTopbar
          :page-title="currentPageTitle"
          :is-sidebar-open="isSidebarOpen"
          :is-dark-mode="isDarkMode"
          @toggle-sidebar="toggleSidebar"
          @toggle-dark-mode="toggleDarkMode"
        />
      </header>

      <div class="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8 py-8 mt-20">
<div class="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4">
  <h1 class="text-2xl sm:text-3xl font-bold text-[#035a76] dark:text-[#4db3ff]">
    School Calendar Management
  </h1>

  <!-- <router-link
    to="/calendar/view"
    class="inline-flex items-center px-5 py-2.5 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg transition shadow-sm"
  >
    <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
    </svg>
    View Full Calendar
  </router-link> -->
</div>
        <!-- Controls -->
        <div class="bg-white dark:bg-gray-800 shadow-sm rounded-xl p-6 border border-gray-200 dark:border-gray-700 mb-8">
          <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
            <!-- Academic Year -->
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Academic Year
              </label>
              <select
                v-model="selectedAcademicYear"
                @change="onAcademicYearChange"
                class="w-full border border-gray-300 dark:border-gray-600 rounded-lg p-2.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              >
                <option value="">Select Academic Year</option>
                <option v-for="year in academicYears" :key="year.id" :value="year.id">
                  {{ year.name }} {{ year.code ? `(${year.code})` : '' }}
                </option>
              </select>
            </div>

            <!-- Term -->
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Term / Semester
              </label>
              <select
                v-model="selectedTerm"
                :disabled="!selectedAcademicYear"
                class="w-full border border-gray-300 dark:border-gray-600 rounded-lg p-2.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              >
                <option value="">Select Term</option>
                <option v-for="term in filteredTerms" :key="term.id" :value="term">
                  {{ term.name }} ({{ formatDate(term.start_date) }} — {{ formatDate(term.end_date) }})
                </option>
              </select>
            </div>

            <!-- Holiday Type -->
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Break / Holiday Type
              </label>
              <select
                v-model="selectedHolidayType"
                :disabled="!selectedTerm"
                class="w-full border border-gray-300 dark:border-gray-600 rounded-lg p-2.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              >
                <option value="">Select Type</option>
                <option v-for="type in holidayTypes" :key="type.id" :value="type">
                  {{ type.name }} {{ type.code ? `(${type.code})` : '' }}
                </option>
              </select>
            </div>

            <!-- Actions -->
            <div class="flex items-end gap-3">
              <button
                @click="applyRange"
                :disabled="!canApply"
                class="px-5 py-2.5 bg-secondary text-white rounded-lg hover:bg-[#024c63] disabled:opacity-50 transition"
              >
                Apply Range
              </button>
              <button
                @click="resetForm"
                class="px-5 py-2.5 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-lg hover:bg-gray-300 transition"
              >
                Clear
              </button>
            </div>
          </div>

          <!-- Date Range -->
          <div v-if="selectedTerm" class="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Start Date
              </label>
              <input
                type="date"
                v-model="rangeStart"
                :min="selectedTerm.start_date"
                :max="selectedTerm.end_date"
                class="w-full border border-gray-300 dark:border-gray-600 rounded-lg p-2.5 bg-white dark:bg-gray-700"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                End Date
              </label>
              <input
                type="date"
                v-model="rangeEnd"
                :min="rangeStart || selectedTerm.start_date"
                :max="selectedTerm.end_date"
                class="w-full border border-gray-300 dark:border-gray-600 rounded-lg p-2.5 bg-white dark:bg-gray-700"
              />
            </div>
          </div>
        </div>

        <!-- Calendar / Status -->
        <div v-if="loading" class="flex justify-center py-12">
          <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-[#035a76]"></div>
        </div>

        <div v-else-if="error" class="bg-red-50 border-l-4 border-red-500 text-red-700 p-6 rounded-lg">
          {{ error }}
        </div>

        <div v-else-if="!selectedTerm" class="text-center py-12 text-gray-500 dark:text-gray-400">
          Select an academic year and term to view and edit the calendar.
        </div>

        <template v-else>
          <!-- Month grids -->
          <div v-for="month in months" :key="month.key" class="mb-10">
            <h2 class="text-xl font-semibold text-[#035a76] dark:text-[#4db3ff] mb-4">
              {{ month.name }} {{ month.year }}
            </h2>
            <div class="grid grid-cols-7 gap-1 text-center">
              <!-- Weekday headers -->
              <div v-for="day in ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']" :key="day"
                   class="font-medium text-gray-600 dark:text-gray-400 py-2">
                {{ day }}
              </div>

              <!-- Days -->
              <template v-for="item in month.days" :key="item.date || 'empty'">
                <div
                  v-if="item.empty"
                  class="p-3 bg-gray-50 dark:bg-gray-800/40"
                ></div>

                <div
                  v-else
                  @click="toggleDay(item)"
                  class="p-3 rounded-lg cursor-pointer border transition min-h-[90px] flex flex-col justify-between"
                  :class="dayClasses(item)"
                >
                  <div class="text-lg font-semibold">
                    {{ item.day }}
                  </div>

                  <div class="text-xs mt-1">
                    <span v-if="item.isWeekend" class="text-gray-500">Weekend</span>
                    <span v-else-if="item.holiday" class="font-medium" :class="holidayColor(item.holiday.holiday_type_id)">
                      {{ item.holiday.name || 'Break' }}
                    </span>
                    <span v-else class="text-green-700 dark:text-green-400">School Day</span>
                  </div>
                </div>
              </template>
            </div>
          </div>

          <!-- Legend -->
          <div class="mt-8 p-4 bg-gray-50 dark:bg-gray-800/40 rounded-lg border border-gray-200 dark:border-gray-700">
            <h3 class="font-medium mb-2">Legend</h3>
            <div class="flex flex-wrap gap-6 text-sm">
              <div class="flex items-center gap-2">
                <div class="w-4 h-4 bg-green-100 dark:bg-green-900/30 border border-green-300 rounded"></div>
                School Day
              </div>
              <div class="flex items-center gap-2">
                <div class="w-4 h-4 bg-gray-200 dark:bg-gray-700 border border-gray-400 rounded"></div>
                Weekend
              </div>
              <div v-for="type in holidayTypes.slice(0,5)" :key="type.id" class="flex items-center gap-2">
                <div class="w-4 h-4 rounded" :class="holidayColor(type.id, true)"></div>
                {{ type.name }}
              </div>
            </div>
          </div>
        </template>
      </div>
    </main>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import AcdSidebar from '@/components/acd/AcdSidebar.vue'
import AcdTopbar from '@/components/acd/AcdTopbar.vue'
import api from '@/services/api' // ← your axios instance

const currentPageTitle = ref('Calendar Management')
const isSidebarOpen = ref(true)
const isDarkMode = ref(false)

const toggleSidebar = () => { isSidebarOpen.value = !isSidebarOpen.value }

// ── Data ────────────────────────────────────────────────
const academicYears = ref([])
const terms = ref([])
const holidayTypes = ref([])

const selectedAcademicYear = ref('')
const selectedTerm = ref(null)        // full term object
const selectedHolidayType = ref(null) // full type object

const rangeStart = ref('')
const rangeEnd = ref('')

const calendarDays = ref([])   // { date, day, isWeekend, holiday? {id, holiday_type_id, name, ...} }
const loading = ref(false)
const error = ref(null)

// ── Computed ───────────────────────────────────────────
const filteredTerms = computed(() => {
  if (!selectedAcademicYear.value) return []
  return terms.value.filter(t => t.academic_year_id == selectedAcademicYear.value)
})

const canApply = computed(() => {
  return selectedTerm.value &&
         selectedHolidayType.value &&
         rangeStart.value &&
         rangeEnd.value &&
         rangeStart.value <= rangeEnd.value
})

const months = computed(() => {
  if (!selectedTerm.value || !calendarDays.value.length) return []

  const groups = {}
  calendarDays.value.forEach(d => {
    if (!d.date) return
    const date = new Date(d.date)
    const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
    const name = date.toLocaleString('default', { month: 'long' })
    if (!groups[key]) {
      groups[key] = {
        key,
        name,
        year: date.getFullYear(),
        days: []
      }
    }
    groups[key].days.push(d)
  })

  // Fill empty slots at beginning of each month
  Object.values(groups).forEach(month => {
    const firstDate = new Date(month.days[0]?.date)
    const firstWeekday = firstDate.getDay() // 0 = Sunday
    for (let i = 0; i < firstWeekday; i++) {
      month.days.unshift({ empty: true })
    }
  })

  return Object.values(groups)
})

// ── Methods ─────────────────────────────────────────────
const fetchAcademicYears = async () => {
  try {
    const res = await api.get('AcademicYears.php')
    academicYears.value = Array.isArray(res.data) ? res.data : []
  } catch (err) {
    console.error(err)
  }
}

const fetchTerms = async () => {
  try {
    const res = await api.get('Terms.php')
    terms.value = Array.isArray(res.data) ? res.data : []
  } catch (err) {
    console.error(err)
  }
}

const fetchHolidayTypes = async () => {
  try {
    const res = await api.get('HolidayTypes.php')
    holidayTypes.value = Array.isArray(res.data) ? res.data : []
  } catch (err) {
    console.error(err)
  }
}

const fetchCalendarForTerm = async (termId) => {
  loading.value = true
  error.value = null

  try {
    // TODO: replace with real endpoint when you create it
    // Example: const res = await api.get(`HolidayCalendar.php?term_id=${termId}`)
    // For now we simulate / placeholder
    const res = await api.get(`HolidayCalendar.php?term_id=${termId}`) // ← implement this

    const saved = Array.isArray(res.data) ? res.data : []

    // Build full day list from term start → end
    calendarDays.value = generateTermDays(termId, saved)
  } catch (err) {
    error.value = 'Could not load calendar entries'
    console.error(err)
  } finally {
    loading.value = false
  }
}

const generateTermDays = (termId, savedHolidays = []) => {
  if (!selectedTerm.value) return []

  const start = new Date(selectedTerm.value.start_date)
  const end = new Date(selectedTerm.value.end_date)
  const days = []

  for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
    const dateStr = d.toISOString().split('T')[0]
    const weekday = d.getDay()
    const isWeekend = weekday === 0 || weekday === 6

    const holiday = savedHolidays.find(h =>
      dateStr >= h.start_date && dateStr <= h.end_date
    )

    days.push({
      date: dateStr,
      day: d.getDate(),
      isWeekend,
      holiday: holiday ? {
        ...holiday,
        name: holidayTypes.value.find(t => t.id == holiday.holiday_type_id)?.name || 'Holiday'
      } : null
    })
  }

  return days
}

const onAcademicYearChange = () => {
  selectedTerm.value = null
  calendarDays.value = []
  rangeStart.value = ''
  rangeEnd.value = ''
}

const applyRange = async () => {
  if (!canApply.value) return

  if (!confirm(`Apply ${selectedHolidayType.value.name} from ${rangeStart.value} to ${rangeEnd.value}?`)) {
    return
  }

  try {
    // TODO: replace with real save endpoint
    const payload = {
      term_id: selectedTerm.value.id,
      holiday_type_id: selectedHolidayType.value.id,
      start_date: rangeStart.value,
      end_date: rangeEnd.value,
      // description: optional
    }

    const res = await api.post('HolidayCalendar.php', payload) // ← create this endpoint

    if (res.data?.status === 'success') {
      // Refresh calendar
      await fetchCalendarForTerm(selectedTerm.value.id)
      alert('Range applied successfully')
      resetForm()
    }
  } catch (err) {
    alert('Failed to save break range')
    console.error(err)
  }
}

const toggleDay = (day) => {
  if (day.isWeekend || !day.date) return
  // Optional: single day quick toggle → would require separate logic
  alert(`Single day click: ${day.date}\nImplement single-day edit if needed.`)
}

const resetForm = () => {
  rangeStart.value = ''
  rangeEnd.value = ''
  selectedHolidayType.value = null
}

const formatDate = (dateStr) => {
  if (!dateStr) return '—'
  return new Date(dateStr).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  })
}

const dayClasses = (day) => {
  if (day.empty) return ''
  if (day.isWeekend) return 'bg-gray-100 dark:bg-gray-800 opacity-60 cursor-default'
  if (day.holiday) {
    return `border-2 border-opacity-70 ${holidayColor(day.holiday.holiday_type_id)} bg-opacity-30`
  }
  return 'bg-green-50 dark:bg-green-950/20 hover:bg-green-100 dark:hover:bg-green-900/30 border-green-200 dark:border-green-800'
}

const holidayColor = (typeId, isLegend = false) => {
  // Simple color mapping – improve with real logic or tailwind classes per type
  const colors = {
    1: 'bg-red-100 border-red-400 text-red-800',     // Christmas
    2: 'bg-purple-100 border-purple-400 text-purple-800', // Easter
    default: 'bg-yellow-100 border-yellow-400 text-yellow-800'
  }
  return colors[typeId] || colors.default
}

const toggleDarkMode = () => {
  isDarkMode.value = !isDarkMode.value;
  if (isDarkMode.value) document.documentElement.classList.add("dark");
  else document.documentElement.classList.remove("dark");
};

// ── Lifecycle ──────────────────────────────────────────
onMounted(async () => {
  await Promise.all([
    fetchAcademicYears(),
    fetchTerms(),
    fetchHolidayTypes()
  ])
})

// Watch term change → load calendar
watch(selectedTerm, (term) => {
  if (term?.id) {
    fetchCalendarForTerm(term.id)
  } else {
    calendarDays.value = []
  }
})
</script>